import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Check, X, ChevronRight, MapPin, Home } from 'lucide-react';
import Icon from './Icon';
import {
  US_STATES, PROPERTY_TYPES, COVERAGE_ITEMS, ADD_ONS, PLANS,
} from '../data/content';
import './QuoteForm.css';

const STEP_LABELS = ['Location', 'Property', 'Coverage', 'Plan', 'Contact'];

/* ── Step bar ── */
function StepBar({ current, total }) {
  return (
    <div className="steps">
      {STEP_LABELS.map((label, i) => {
        const s     = i + 1;
        const done  = current > s;
        const active = current === s;
        return (
          <React.Fragment key={s}>
            <div className="step-item">
              <div className={`sdot ${done ? 'sdot-done' : active ? 'sdot-active' : 'sdot-idle'}`}>
                {done ? <Check size={13} strokeWidth={3} /> : s}
              </div>
              <span className={`step-lbl ${active ? 'step-lbl-on' : done ? 'step-lbl-done' : ''}`}>
                {label}
              </span>
            </div>
            {i < total - 1 && (
              <div className={`sline ${done ? 'sline-done' : 'sline-idle'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ── Step 1: Location (ZIP-first) ── */
function Step1({ form, set, onNext }) {
  const ready = form.zip.length === 5 && form.state;

  /* auto-advance when 5 digits + state already chosen */
  const handleZip = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    set('zip', val);
  };

  return (
    <div className="step-body">
      <div className="step-hero-label">
        <MapPin size={18} color="var(--sky)" strokeWidth={2} />
        Where is your property located?
      </div>

      <div className="zip-row">
        <div className="fg zip-fg">
          <label className="fl">ZIP Code <span className="req">*</span></label>
          <input
            className="fi fi-lg"
            maxLength={5}
            placeholder="e.g.  90210"
            value={form.zip}
            onChange={handleZip}
            autoFocus
          />
        </div>
        <div className="fg state-fg">
          <label className="fl">State <span className="req">*</span></label>
          <select className="fs fi-lg" value={form.state} onChange={(e) => set('state', e.target.value)}>
            <option value="">Select state</option>
            {US_STATES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="fg">
        <label className="fl">Property Address</label>
        <input
          className="fi"
          placeholder="123 Main Street"
          value={form.address}
          onChange={(e) => set('address', e.target.value)}
        />
      </div>

      <button className="btn btn-sky btn-full" disabled={!ready} onClick={onNext}>
        Continue <ChevronRight size={17} strokeWidth={2.5} />
      </button>
      <p className="form-note">
        <Lock size={12} strokeWidth={2} /> Secure &amp; private · No spam · No obligation
      </p>
    </div>
  );
}

/* ── Step 2: Property Details ── */
function Step2({ form, set, onBack, onNext }) {
  return (
    <div className="step-body">
      <div className="step-hero-label">
        <Home size={18} color="var(--sky)" strokeWidth={2} />
        Tell us about your property
      </div>

      <div className="fg">
        <label className="fl">Property Type</label>
        <div className="opt-grid">
          {PROPERTY_TYPES.map((p) => (
            <button
              key={p.label}
              className={`opt-btn ${form.propertyType === p.label ? 'sel' : ''}`}
              onClick={() => set('propertyType', p.label)}
            >
              <Icon name={p.icon} size={17} strokeWidth={1.75} />
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="frow">
        <div className="fg">
          <label className="fl">Square Footage</label>
          <select className="fs" value={form.sqft} onChange={(e) => set('sqft', e.target.value)}>
            <option value="">Select size</option>
            {['Under 1,000 sq ft','1,000 – 1,500 sq ft','1,500 – 2,000 sq ft',
              '2,000 – 3,000 sq ft','3,000 – 5,000 sq ft','Over 5,000 sq ft'].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </div>
        <div className="fg">
          <label className="fl">Year Built</label>
          <select className="fs" value={form.yearBuilt} onChange={(e) => set('yearBuilt', e.target.value)}>
            <option value="">Select year</option>
            {['2020 – Present','2010 – 2019','2000 – 2009','1990 – 1999',
              '1980 – 1989','1970 – 1979','Before 1970'].map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="action-row">
        <button className="btn btn-back btn-auto" onClick={onBack}>← Back</button>
        <button className="btn btn-sky btn-grow" onClick={onNext}>
          Continue <ChevronRight size={17} strokeWidth={2.5} />
        </button>
      </div>
      <p className="form-note">
        <Lock size={12} strokeWidth={2} /> Secure &amp; private · No spam · No obligation
      </p>
    </div>
  );
}

/* ── Step 3: Coverage ── */
function Step3({ form, toggle, onBack, onNext }) {
  return (
    <div className="step-body">
      <div className="fg">
        <label className="fl fl-lg">What would you like covered?</label>
        <div className="cb-grid">
          {COVERAGE_ITEMS.map((item) => {
            const on = form.coverageItems.includes(item);
            return (
              <label key={item} className={`cb-item ${on ? 'chk' : ''}`}
                onClick={() => toggle('coverageItems', item)}>
                <span className="cb-check">{on && <Check size={11} strokeWidth={3} />}</span>
                {item}
              </label>
            );
          })}
        </div>
      </div>

      <div className="fg">
        <label className="fl fl-lg">Optional add-ons</label>
        <div className="cb-grid">
          {ADD_ONS.map((item) => {
            const on = form.addOns.includes(item);
            return (
              <label key={item} className={`cb-item ${on ? 'chk' : ''}`}
                onClick={() => toggle('addOns', item)}>
                <span className="cb-check">{on && <Check size={11} strokeWidth={3} />}</span>
                {item}
              </label>
            );
          })}
        </div>
      </div>

      <div className="action-row">
        <button className="btn btn-back btn-auto" onClick={onBack}>← Back</button>
        <button className="btn btn-sky btn-grow" onClick={onNext}>
          See Plans <ChevronRight size={17} strokeWidth={2.5} />
        </button>
      </div>
      <p className="form-note">
        <Lock size={12} strokeWidth={2} /> Secure &amp; private · No spam · No obligation
      </p>
    </div>
  );
}

/* ── Step 4: Plan selector ── */
function Step4({ form, set, onBack, onNext }) {
  return (
    <div className="step-body">
      <p className="plan-hint">Which coverage level fits your home best?</p>
      {PLANS.map((p) => (
        <div
          key={p.name}
          className={`plan-opt ${form.plan === p.name ? 'sel' : ''} ${p.popular ? 'pop' : ''}`}
          onClick={() => set('plan', p.name)}
        >
          {p.popular && <div className="pop-tag">Most Popular</div>}
          <div className="plan-row">
            <div>
              <div className="plan-name">{p.name}</div>
              <div className="plan-desc">{p.desc}</div>
            </div>
            <div className={`plan-radio ${form.plan === p.name ? 'plan-radio-sel' : ''}`}>
              {form.plan === p.name && <Check size={12} strokeWidth={3} />}
            </div>
          </div>
          <div className="plan-feat">
            {p.features.map((f) => (
              <div key={f.text} className={`plan-fi ${f.included ? '' : 'plan-fi-off'}`}>
                {f.included
                  ? <Check size={12} strokeWidth={2.5} color="var(--green)" />
                  : <X     size={12} strokeWidth={2.5} color="var(--g300)" />
                }
                {f.text}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="action-row">
        <button className="btn btn-back btn-auto" onClick={onBack}>← Back</button>
        <button className="btn btn-sky btn-grow" disabled={!form.plan} onClick={onNext}>
          Continue <ChevronRight size={17} strokeWidth={2.5} />
        </button>
      </div>
      <p className="form-note">
        <Lock size={12} strokeWidth={2} /> Secure &amp; private · No spam · No obligation
      </p>
    </div>
  );
}

/* ── Step 5: Contact ── */
function Step5({ form, set, onBack, onSubmit, loading }) {
  const ready = form.firstName.trim() && form.email.trim() && form.phone.trim();

  return (
    <div className="step-body">
      <div className="frow">
        <div className="fg">
          <label className="fl">First Name <span className="req">*</span></label>
          <input className="fi" placeholder="Jane" value={form.firstName}
            onChange={(e) => set('firstName', e.target.value)} />
        </div>
        <div className="fg">
          <label className="fl">Last Name</label>
          <input className="fi" placeholder="Smith" value={form.lastName}
            onChange={(e) => set('lastName', e.target.value)} />
        </div>
      </div>

      <div className="fg">
        <label className="fl">Email Address <span className="req">*</span></label>
        <input className="fi" type="email" placeholder="jane@email.com" value={form.email}
          onChange={(e) => set('email', e.target.value)} />
      </div>

      <div className="fg">
        <label className="fl">Phone Number <span className="req">*</span></label>
        <input className="fi" type="tel" placeholder="(555) 000-0000" value={form.phone}
          onChange={(e) => set('phone', e.target.value)} />
      </div>

      <div className="fg">
        <label className="fl">Best time to contact</label>
        <select className="fs" value={form.contactTime} onChange={(e) => set('contactTime', e.target.value)}>
          <option>Anytime</option>
          <option>Morning (8AM – 12PM)</option>
          <option>Afternoon (12PM – 5PM)</option>
          <option>Evening (5PM – 8PM)</option>
        </select>
      </div>

      <div className="action-row">
        <button className="btn btn-back btn-auto" onClick={onBack}>← Back</button>
        <button className="btn btn-gold btn-grow" disabled={!ready || loading} onClick={onSubmit}>
          {loading
            ? 'Submitting…'
            : <><Lock size={14} strokeWidth={2} style={{ marginRight: 6 }} />Get My Free Quote</>
          }
        </button>
      </div>
      <p className="form-note">
        <Lock size={12} strokeWidth={2} /> By submitting you agree to receive calls &amp; emails about your quote. No spam.
      </p>
    </div>
  );
}

/* ── Main QuoteForm ── */
const INITIAL = {
  zip: '', state: '',
  propertyType: '', address: '', sqft: '', yearBuilt: '',
  coverageItems: [], addOns: [],
  plan: '',
  firstName: '', lastName: '', email: '', phone: '', contactTime: 'Anytime',
};

export default function QuoteForm() {
  const navigate = useNavigate();
  const [step, setStep]   = useState(1);
  const [form, setForm]   = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const toggle = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((x) => x !== value) : [...f[key], value],
    }));

  const handleSubmit = () => {
    setLoading(true);
    setTimeout(() => { navigate('/thank-you'); }, 1600);
  };

  const TOTAL = STEP_LABELS.length;

  return (
    <div className="qcard" id="quote-section">
      <div className="qcard-header">
        <div className="qcard-title">Get Your Free Quote</div>
        <div className="qcard-sub">{`Step ${step} of ${TOTAL} — ${STEP_LABELS[step - 1]}`}</div>
      </div>

      <StepBar current={step} total={TOTAL} />

      {step === 1 ? (
        <Step1 form={form} set={set} onNext={() => setStep(2)} />
      ) : step === 2 ? (
        <Step2 form={form} set={set} onBack={() => setStep(1)} onNext={() => setStep(3)} />
      ) : step === 3 ? (
        <Step3 form={form} toggle={toggle} onBack={() => setStep(2)} onNext={() => setStep(4)} />
      ) : step === 4 ? (
        <Step4 form={form} set={set} onBack={() => setStep(3)} onNext={() => setStep(5)} />
      ) : (
        <Step5 form={form} set={set} onBack={() => setStep(4)} onSubmit={handleSubmit} loading={loading} />
      )}
    </div>
  );
}
